import { __assign } from "tslib";
import { logNotification } from '@microsoft/oteljs';
import { oteljsVersion } from './Version';
import { isNumeric } from './Utils';
/**
 * This class is ONLY for use with the WebSink and MUST NOT be exposed publicly.
 * It is approved by the Collector team for sending a maximum of one batch per session.
 * See comments on sendEvents.
 */
// There are two limits: a count of events (imposed by the Collector)
// and a byte limit for sendBeacon (TODO: Is there also a byte limit for Collector?).
var MAX_EVENTS = 500;
var MAX_BEACON_SIZE = 65536; // Typically browsers have a 64KB limit
var MAX_PAYLOAD_SIZE = 4194304; // 4MB limit on the Collector side
// Callers aren't allowed to use DateTime, but we can.
var DATETIME_SPECIAL_TYPE = 'dt';
var DATA_NAMESPACE_TOKEN = 'Data';
var CONTRACT_NAMESPACE_TOKEN = 'zC';
var EVENT_NAMESPACE_TOKEN = 'Event';
var NAME_TOKEN = 'Name';
var SEQUENCE_TOKEN = 'Sequence';
var TIME_TOKEN = 'Time';
var ID_TOKEN = 'Id';
var SOURCE_TOKEN = 'Source';
var CONTRACT_TOKEN = 'Contract';
var SEPARATOR_TOKEN = '.';
var SOURCE_NAME = 'OTelJS';
var SINK_NAME = SOURCE_NAME + 'Min';
var MINSINK_LOGGING_TOKEN = SINK_NAME + ':';
var POST_METHOD = 'POST';
var NEWLINE = '\n';
// Only allow one batch to be sent
var sendEventWasInvoked = false;
// Exported ONLY for use in testing
export function reset() {
    sendEventWasInvoked = false;
}
function isObject(item) {
    return typeof item === 'object';
}
/**
 * Processes ITypedTelemetryEvents (OTel) into IExtendedTelemetryEvents (1DS/CommonSchema).
 * Control flow is getTelemetryItems -> getTelemetryItem -> addFieldsToTelemetryItem -> addFieldToTelemetryItem
 * @param webSinkProps WebSink properties
 * @param minSinkProps MinSink properties
 * @param events The events to process into telemetry items
 * @returns An array of IExtendedTelemetryEvent items and an array of tenant tokens in those events
 */
export function getTelemetryItems(webSinkProps, minSinkProps, events) {
    /*
     * Helper functions start here. Main function implementation is below
     */
    var _a, _b;
    /**
     * Adds a value and property type to the metadata and data objects on the telemetry item
     * @param nameTokens - An array of tokens representing the name of the data field
     * @param value - The value
     * @param dataType - The data field type
     * @param telemetryItem - The telemetry item
     * @returns - void
     */
    function addFieldToTelemetryItem(nameTokens, value, dataType, telemetryItem) {
        var _a;
        var valueToWrite = value;
        var eventPropertyType = 0 /* Unspecified */;
        // Skip empty fields
        if (typeof valueToWrite === 'undefined' || valueToWrite === '') {
            return;
        }
        // Some clients send numeric values but declare them as strings, which doesn't work
        // Send them as double
        // If this step is skipped, values like Data.CacheId = -15325 will get dropped.
        if (isNumeric(value)) {
            eventPropertyType = 6 /* Double */;
        }
        switch (dataType) {
            case 3 /* Double */:
                eventPropertyType = 6 /* Double */;
                break;
            case 2 /* Int64 */:
                eventPropertyType = 4 /* Int64 */;
                break;
            case 4 /* Guid */:
                eventPropertyType = 8 /* Guid */;
                break;
            case DATETIME_SPECIAL_TYPE:
                eventPropertyType = 9 /* DateTime */;
                break;
            case 0 /* String */:
                // Limit the maximum allowed string length on a datafield to avoid accidentally going over
                // the maximum allowed serialized size for a 1DS event and having the event dropped.
                valueToWrite = typeof valueToWrite === 'string' ? valueToWrite.substr(0, 25000) : valueToWrite;
                break;
            case 1 /* Boolean */:
            default:
                break;
        }
        if (enableCompoundKey) {
            var dataIterator = telemetryItem.data;
            var metadataIterator = telemetryItem.ext.metadata;
            var tokenIndex = 0;
            while (tokenIndex < nameTokens.length - 1) {
                var token_1 = nameTokens[tokenIndex];
                if (!token_1) {
                    return;
                }
                if (dataIterator[token_1]) {
                    if (!isObject(dataIterator)) {
                        // We're trying to add an object to a thing that already has a value
                        return;
                    }
                }
                else {
                    dataIterator[token_1] = {};
                }
                dataIterator = dataIterator[token_1];
                var metadataF = metadataIterator.f || {};
                if (!metadataF[token_1]) {
                    metadataF[token_1] = {};
                }
                metadataIterator.f = metadataF;
                metadataIterator = metadataF[token_1];
                tokenIndex++;
            }
            var token = nameTokens[tokenIndex];
            if (isObject(dataIterator) && !isObject(dataIterator[token])) {
                dataIterator[token] = valueToWrite;
                if (eventPropertyType) {
                    metadataIterator.f = __assign(__assign({}, metadataIterator.f), (_a = {}, _a[token] = {
                        t: eventPropertyType
                    }, _a));
                }
            }
        }
        else {
            var fieldName = nameTokens.join(SEPARATOR_TOKEN);
            telemetryItem.data[fieldName] = value;
            if (eventPropertyType) {
                telemetryItem.ext.metadata.f[fieldName] = {
                    t: eventPropertyType
                };
            }
        }
    }
    function addFieldsToTelemetryItem(fields, prependDataToken, telemetryItem) {
        if (fields) {
            fields.forEach(function (field) {
                // We can usually only send SystemMetadata or EssentialServiceMetadata
                // We drop other fields
                var dataClassification = field.classification;
                if (!(dataClassification === 4 /* SystemMetadata */ || dataClassification === 1 /* EssentialServiceMetadata */)) {
                    return;
                }
                var fieldNameTokens = field.name.split(SEPARATOR_TOKEN);
                if (fieldNameTokens[0] === CONTRACT_NAMESPACE_TOKEN) {
                    return;
                }
                if (prependDataToken) {
                    fieldNameTokens.unshift(DATA_NAMESPACE_TOKEN);
                }
                addFieldToTelemetryItem(fieldNameTokens, field.value, field.dataType, telemetryItem);
            });
        }
    }
    function getTelemetryItem(typedTelemetryEvent, tenantToken, eventSequence, sessionId) {
        if (typedTelemetryEvent.t !== 1 /* TelemetryEvent */) {
            // In particular, we can't send CustomerContentEvents through this sink
            return undefined;
        }
        var telemetryEvent = typedTelemetryEvent.event;
        var inputTimestamp = telemetryEvent.timestamp;
        var time = (inputTimestamp ? new Date(inputTimestamp) : new Date()).toISOString();
        var diagnosticLevel = telemetryEvent.eventFlags.diagnosticLevel;
        if (!webSinkProps.enableOptionalEvents &&
            diagnosticLevel !== 10 /* Required */ &&
            diagnosticLevel !== 110 /* RequiredServiceData */ &&
            diagnosticLevel !== 120 /* RequiredServiceDataForEssentialServices */) {
            return undefined;
        }
        var eventName = telemetryEvent.eventName;
        var telemetryItem = {
            name: eventName,
            time: time,
            ver: '4.0',
            // The most significant difference between ITelemetryItem and events over the wire is here -
            // ITelemetryItem expects events without 'o:', and then appends 'o:' before sending them.
            // We start with 'o:'
            iKey: 'o:' + tenantToken.substr(0, 32),
            ext: {
                sdk: {
                    seq: eventSequence
                },
                metadata: {
                    f: {}
                }
            },
            data: {}
        };
        addFieldToTelemetryItem([EVENT_NAMESPACE_TOKEN, SOURCE_TOKEN], SOURCE_NAME, 0 /* String */, telemetryItem);
        addFieldToTelemetryItem(['baseData', 'properties', 'version'], SINK_NAME + '=' + oteljsVersion, 0 /* String */, telemetryItem);
        addFieldsToTelemetryItem(webSinkProps.persistentDataFields, /* prependDataToken */ false, telemetryItem);
        // Handle Part A fields that change per-event
        addFieldToTelemetryItem([EVENT_NAMESPACE_TOKEN, NAME_TOKEN], eventName, 0 /* String */, telemetryItem);
        addFieldToTelemetryItem([EVENT_NAMESPACE_TOKEN, SEQUENCE_TOKEN], eventSequence, 2 /* Int64 */, telemetryItem);
        addFieldToTelemetryItem([EVENT_NAMESPACE_TOKEN, TIME_TOKEN], time, DATETIME_SPECIAL_TYPE, telemetryItem);
        addFieldToTelemetryItem([EVENT_NAMESPACE_TOKEN, ID_TOKEN], sessionId + SEPARATOR_TOKEN + eventSequence, 0 /* String */, telemetryItem);
        // Handle the baseType field
        var baseType = 'custom';
        var contract = telemetryEvent.eventContract;
        if (contract) {
            var contractName = contract.name;
            addFieldToTelemetryItem([EVENT_NAMESPACE_TOKEN, CONTRACT_TOKEN], contractName, 0 /* String */, telemetryItem);
            baseType += SEPARATOR_TOKEN + contractName.toLowerCase().replace(/\./g, '_');
            addFieldsToTelemetryItem(contract.dataFields, /*prependDataToken*/ false, telemetryItem);
        }
        telemetryItem.data['baseType'] = baseType;
        // Handle Part C fields
        addFieldsToTelemetryItem(telemetryEvent.dataFields, /* prependDataToken*/ true, telemetryItem);
        return telemetryItem;
    }
    /**
     * Main implementation of getTelemetryItems starts here
     */
    // Match what the PostChannel would have been doing
    // See the definition of enableCompoundKey there
    var enableCompoundKey = ((_b = (_a = webSinkProps.extensionConfig) === null || _a === void 0 ? void 0 : _a.PostChannel) === null || _b === void 0 ? void 0 : _b.enableCompoundKey) === true;
    var telemetryItems = [];
    var uniqueTenantTokens = {};
    var minSinkSessionId = minSinkProps.uuid();
    // Limit the number of events to send to MAX_EVENTS or minSinkLimit (if it is set)
    // If minSinkLimit is higher than MAX_EVENTS, that's fine - the number of events will
    // still be limited by the beacon byte limit, which is stricter.
    var eventLimit = webSinkProps.minSinkLimit;
    eventLimit = isNumeric(eventLimit) ? eventLimit : MAX_EVENTS;
    var eventsToSend = events.slice(0, eventLimit);
    // eventSequence is 1-indexed
    var eventSequence = minSinkProps.stats["pw" /* PassedToWebWorker */] + 1;
    eventsToSend.forEach(function (event) {
        var tenantToken = event.event.telemetryProperties.ariaTenantToken;
        var telemetryItem = getTelemetryItem(event, tenantToken, eventSequence, minSinkSessionId);
        if (telemetryItem) {
            telemetryItems.push(telemetryItem);
            // This is essentially a polyfill for Set(), which doesn't appear until ES6
            uniqueTenantTokens[tenantToken] = true;
            eventSequence = eventSequence + 1;
        }
    });
    return [telemetryItems, Object.keys(uniqueTenantTokens)];
}
/**
 * Gets a payload to send to the collector
 * This should only be called once per sendEvents() invocation
 * Side-effect: stats[DataModels.WebSinkStat.MinSinkProcessed] is set to the number of events added to the payload
 * @param telemetryItems The telemetry items to process
 * @param maxBytes - The maximum number of bytes in the payload
 * @param stats - Stats to modify
 * @returns - The payload
 */
function getPayload(telemetryItems, maxBytes, stats) {
    var payload = '';
    var itemSeparator = '';
    for (var _i = 0, telemetryItems_1 = telemetryItems; _i < telemetryItems_1.length; _i++) {
        var telemetryItem = telemetryItems_1[_i];
        var newPayload = payload + itemSeparator + JSON.stringify(telemetryItem);
        var totalLength = newPayload.length;
        // Beacons have a browser-imposed limit. Each batch has a Collector-imposed limit.
        if (totalLength > maxBytes) {
            // This will be inferred by PassedToMinSink - MinSinkProcessed
            break;
        }
        payload = newPayload;
        stats["sp" /* MinSinkProcessed */] += 1;
        itemSeparator = NEWLINE;
    }
    return payload;
}
function sendUsingXhr(endpointUrl, telemetryItems, stats) {
    var payload = getPayload(telemetryItems, MAX_PAYLOAD_SIZE, stats);
    var xhRequest = new XMLHttpRequest();
    xhRequest.onload = function () {
        var responseText = xhRequest.responseText;
        if (!responseText) {
            return;
        }
        var response = JSON.parse(responseText);
        if (response.acc) {
            stats["ss" /* MinSinkSent */] += parseInt(response.acc, 10);
        }
        if (response.rej) {
            stats["sd" /* MinSinkDropped */] += parseInt(response.rej, 10);
        }
    };
    xhRequest.onerror = function () {
        // Assume all of the events got dropped
        stats["sd" /* MinSinkDropped */] = stats["sp" /* MinSinkProcessed */];
    };
    xhRequest.open(POST_METHOD, endpointUrl);
    xhRequest.send(payload);
}
function sendUsingBeacon(endpointUrl, telemetryItems, stats) {
    var itemCount = telemetryItems.length;
    if (Boolean(navigator === null || navigator === void 0 ? void 0 : navigator.sendBeacon)) {
        var sendBeacon = function (endpointUrl, payload) {
            return navigator.sendBeacon(endpointUrl, payload);
        };
        // First, try to send all the events in one beacon
        var payload = getPayload(telemetryItems, MAX_BEACON_SIZE, stats);
        if (sendBeacon(endpointUrl, payload)) {
            stats["ss" /* MinSinkSent */] = stats["sp" /* MinSinkProcessed */];
        }
        else {
            // If the single beacon failed, send events individually
            var beaconSendCount = 0;
            while (beaconSendCount < itemCount) {
                var payload_1 = JSON.stringify(telemetryItems[beaconSendCount]);
                if (!sendBeacon(endpointUrl, payload_1)) {
                    break;
                }
                beaconSendCount++;
            }
            stats["ss" /* MinSinkSent */] = beaconSendCount;
            stats["sd" /* MinSinkDropped */] = stats["sp" /* MinSinkProcessed */] - beaconSendCount;
        }
    }
    else {
        stats["sd" /* MinSinkDropped */] = itemCount;
    }
}
/**
 * sendEvents() MUST NOT be invoked more than once per session. Multiple invocations are not approved by the Collector team.
 * The MinSink module exposes one function, sendEvents(), that is capable of sending one batch of events to the collector per session.
 * This is a minimal implementation of a telemetry SDK, and does not support batching, interpretation of server responses
 *
 * Stats:
 * The caller logs PassedToMinSink.
 * The MinSink logs:
 *   MinSinkProcessed - number of events added to a batch (calculated in getPayload)
 *   MinSinkSent - number of events sent (either by completion of sendBeacon or by waiting for an 'acc' response from sendXhr)
 *   MinSinkDropped - number of events reported dropped by a 'rej' response from sendXhr
 *
 * PassedToMinSink
 *
 * @param webSinkProps - Properties of the web sink that invoked this min sink
 * @param minSinkProps - Properties specific to the min sink
 * @param events - the events to send
 * @returns - void
 */
export function sendEvents(webSinkProps, minSinkProps, events) {
    if (sendEventWasInvoked) {
        // Only one batch is allowed
        logNotification(0 /* Error */, 1 /* Sink */, function () { return MINSINK_LOGGING_TOKEN + 20 /* MinSinkReused */; });
        return;
    }
    try {
        var _a = getTelemetryItems(webSinkProps, minSinkProps, events), telemetryItems = _a[0], uniqueTenantTokens = _a[1];
        if (telemetryItems.length === 0 || uniqueTenantTokens.length === 0) {
            return;
        }
        var endpointUrlWithQueryString = webSinkProps.endpointUrl + '?cors=true&content-type=application/x-json-stream&apikey=' + uniqueTenantTokens.join();
        var stats = minSinkProps.stats;
        if (minSinkProps.useSendBeacon) {
            sendUsingBeacon(endpointUrlWithQueryString, telemetryItems, stats);
        }
        else {
            sendUsingXhr(endpointUrlWithQueryString, telemetryItems, stats);
        }
        sendEventWasInvoked = true;
    }
    catch (_e) {
        logNotification(0 /* Error */, 1 /* Sink */, function () { return MINSINK_LOGGING_TOKEN + 22 /* MinSinkThrows */; });
    }
}
//# sourceMappingURL=MinSink.js.map